import { Tag, Typography } from '@quickbit/qb-design-system';
import { CodeBlock } from '../CodeBlock';
import { InlineCode } from '../InlineCode';
import { PageText } from './types';

const createDirectOnrampFlowOrderExample = `{
  "euro_amount": 100.00,
  "callback_url": "https://my-domain.com/api/quickbit-callback",
  "email_address": "roger@quickbit.com", // <-- This is required in the direct-onramp flow
  "purchased_at": "2023-04-12T06:23:03.558Z", // <-- When the fiat payment was made
  "reference_id": "your-reference-id" // <-- Your reference ID
}`;

const createDirectOnrampFlowOrderResponseExample = `{
  "euro_amount": 100.00,
  "callback_url": "https://my-domain.com/api/quickbit-callback",
  "email_address": "roger@quickbit.com", // <-- This is required in the direct-onramp flow
  "purchased_at": "2023-04-12T06:23:03.558Z", // <-- When the fiat payment was made
  "reference_id": "your-reference-id", // <-- Your reference ID
  "state": "REQUEST_CREATED", // <-- The current state of the transaction request
  "created_at": "2023-04-12T06:23:07.558Z", // <-- When the transaction request was created
  "user_id": null
}`;

const getDirectOnrampFlowOrderResponseExample = `{
  "euro_amount": 100.00,
  "callback_url": "https://my-domain.com/api/quickbit-callback",
  "email_address": "roger@quickbit.com", // <-- This is required in the direct-onramp flow
  "purchased_at": "2023-04-12T06:23:03.558Z", // <-- When the fiat payment was made
  "reference_id": "your-reference-id", // <-- Your reference ID
  "state": "COMPLETED", // <-- The current state of the transaction request
  "created_at": "2023-04-12T06:23:07.558Z", // <-- When the transaction request was created
  "user_id": null
}`;

const apiKeyExample = `{
  "X-Api-Key": "your-api-key-here"
}`;

const callbackExample = `{
  "reference_id": "your-reference-id", // <-- Your reference ID
  "type": "COMPLETED", // <-- The current state of the transaction request
}`;

export const directOnrampFlowDocumentation: Array<PageText> = [
  {
    id: 'direct-onramp-flow',
    title: 'Direct Onramp flow',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: <Typography variant="heading1">Direct Onramp flow</Typography>,
  },
  {
    id: 'creating-a-direct-onramp-transaction-request',
    title: 'Creating a Direct Onramp Transaction Request',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: (
      <Typography variant="heading2">
        Creating a Direct Onramp Transaction Request
      </Typography>
    ),
  },
  {
    id: 'direct-onramp-flow-domains',
    title: 'Direct Onramp Flow Domains',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: (
      <>
        <Typography variant="body">
          The domains for the Direct Onramp flow are:
        </Typography>
        <ul>
          <li>
            <InlineCode>https://api.travelruleserver.com</InlineCode> - for PROD
          </li>
          <li>
            <InlineCode>https://skinning-api.test.bazoro.com</InlineCode> - for
            TEST
          </li>
        </ul>
        <br></br>
      </>
    ),
  },
  {
    id: 'direct-onramp-flow-endpoint',
    title: 'Direct Onramp Flow Endpoint',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: (
      <>
        <Typography variant="body">
          Direct Onramp transaction requestss are created using the{' '}
          <InlineCode>POST /direct-onramp/v1/transaction-requests</InlineCode>{' '}
          endpoint. This endpoint requires authentication using your API key in
          the <InlineCode>X-Api-Key</InlineCode> header.
        </Typography>
        <CodeBlock language="json" code={apiKeyExample} />
        <br></br>
      </>
    ),
  },
  {
    id: 'direct-onramp-flow-request-body',
    title: 'Request Body',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: <Typography variant="subtitle">Request Body</Typography>,
  },
  {
    id: 'direct-onramp-flow-request-body-content',
    title: 'Request Body',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: (
      <>
        <Typography variant="body">
          The request body should contain the transaction request details as
          JSON. All fields are required.
        </Typography>
        <ul>
          <li>
            <InlineCode>euro_amount</InlineCode> - The amount to charge in EUR,
            max 2 decimal places.
          </li>
          <li>
            <InlineCode>email_address</InlineCode> - Customer email address
          </li>
          <li>
            <InlineCode>callback_url</InlineCode> - URL to receive order status
            updates
          </li>
          <li>
            <InlineCode>reference_id</InlineCode> - Your internal order ID
          </li>
          <li>
            <InlineCode>purchased_at</InlineCode> - Timestamp when fiat payment
            was made
          </li>
        </ul>
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-a-direct-onramp-order-1',
    title: 'Creating a direct-onramp transaction request example',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: (
      <>
        <CodeBlock language="json" code={createDirectOnrampFlowOrderExample} />
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-a-direct-onramp-order-1',
    title: 'Creating a direct-onramp transaction request response example',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: (
      <>
        <Typography>
          The response will contain the transaction request details including a
          state.
        </Typography>
        <CodeBlock
          language="json"
          code={createDirectOnrampFlowOrderResponseExample}
        />
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-a-direct-onramp-order-2',
    title: 'Creating a direct-onramp order',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: (
      <>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography>The direct-onramp flow uses Algorand.</Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'fetching-a-transaction-request',
    title: 'Fetching a transaction request',
    page: 'integration',
    content: (
      <>
        <Typography variant="heading2">
          Fetching a transaction request
        </Typography>
        <Typography variant="body">
          In case a transaction request needs to be retrieved in the future you
          can do so using the{' '}
          <InlineCode>
            {'GET /direct-onramp/v1/transaction-requests/{reference_id}'}
          </InlineCode>{' '}
          endpoint. As can be seen, the endpoint requires the reference ID.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'fetching-an-transaction-request-2',
    title: 'Fetching a transaction request',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: (
      <>
        <Typography variant="body">
          The endpoint for fetching orders will return the same type of response
          as the endpoint for creating orders. An example can be seen below.
        </Typography>
        <CodeBlock
          language="json"
          code={getDirectOnrampFlowOrderResponseExample}
        />
        <br></br>
      </>
    ),
  },

  {
    id: 'callback-events-1',
    title: 'Callback events',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: (
      <>
        <Typography variant="heading2">Callback events</Typography>
        <Typography variant="body">
          Our system will notify you with a <InlineCode>POST</InlineCode>{' '}
          request sent to the specified <InlineCode>callback_url</InlineCode>{' '}
          when the transaction request is completed. The callback will not have
          any specific authentication, so it is important that you validate the
          request by doing a{' '}
          <InlineCode>
            {'GET /direct-onramp/v1/transaction-requests/{reference_id}'}
          </InlineCode>{' '}
          and checking that the state is <InlineCode>COMPLETED</InlineCode>.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'callback-events-2',
    title: 'Callback events',
    page: 'integration',
    subPage: 'direct-onramp-flow',
    content: (
      <>
        <Typography variant="body">
          The callback event will contain a body with the following properties:
          the reference ID and the callback type. An example body is shown
          below.
        </Typography>
        <CodeBlock language="json" code={callbackExample} />
        <br></br>
      </>
    ),
  },
];
